import { injectAsyncReducer } from '../../hermes-redux';
import USER_ROLES from 'constants/user-roles.constant';
import { restrictions } from 'state/features-list/constants';
import { CarePathRestrictions, MyCareRoutes } from './my-care.constants';
import { isFeatureEnabled } from 'stores/features-list-store';

const createResolverWithMoodTrackingReducer = pageImport =>
  Promise.all([
    pageImport,
    import(
      /* webpackChunkName: "mood-tracking-reducer" */ '../mood-tracking/state/reducer'
    ),
  ]).then(([component, reducer]) => {
    injectAsyncReducer('moodTracking', reducer.default);
    return component;
  });

const createResolver = pageImport =>
  Promise.all([pageImport]).then(([component]) => {
    return component;
  });

const routeUrl = '/care-path';

const myCareModule = {
  hub: {
    name: MyCareRoutes.MY_CARE_HUB,
    url: routeUrl,
    resolver: () =>
      createResolverWithMoodTrackingReducer(
        import(
          /* webpackChunkName: "my-care-hub" */ '../my-care/container/my-care-hub/my-care-hub'
        ),
      ),
    restrictions: {
      user: [
        restrictions['care_plans'],
        restrictions['counsellor_appointments'],
        restrictions['care_coordination_specialists_messaging'],
      ],
      operator: 'OR',
    },
    data: {
      title: 'components.main_menu.care_path',
      section: 'myCare',
      mainMenu: 'myCare',
      includeInMenu: true,
      hideFooter: true,
      order: 0,
      authorizedRoles: [USER_ROLES.employee],
    },
  },
  otherServices: {
    name: MyCareRoutes.MY_CARE_OTHER_SERVICES,
    url: `${routeUrl}/other-services`,
    resolver: () =>
      createResolver(
        import(
          /* webpackChunkName: "my-care-other-services" */ '../my-care/container/other-services/other-services-container'
        ),
      ),
    restrictions: {
      user: [
        restrictions['care_plans'],
        restrictions['counsellor_appointments'],
        restrictions['care_coordination_specialists_messaging'],
      ],
      operator: 'OR',
    },
    data: {
      title: 'care_plan.work_life_services.title',
      includeInMenu: false,
      hideFooter: true,
      authorizedRoles: [USER_ROLES.employee],
    },
  },
  assessmentResults: {
    name: MyCareRoutes.MY_CARE_ASSESMENT_RESULTS,
    url: `${routeUrl}/assessments`,
    resolver: () =>
      createResolver(
        import(
          /* webpackChunkName: "my-care-assessment-results" */ '../my-care/container/assessment-results/assessment-results'
        ),
      ),
    restrictions: {
      user: [
        restrictions['care_plans'],
        restrictions['counsellor_appointments'],
        restrictions['care_coordination_specialists_messaging'],
      ],
      operator: 'OR',
    },
    data: {
      title: 'my_care.assessment_results.title',
      includeInMenu: false,
      hideFooter: true,
      authorizedRoles: [USER_ROLES.employee],
    },
  },
  bookAppointment: {
    name: MyCareRoutes.BOOK_APPOINTMENT,
    url: '/tmh/book-appointment',
    resolver: () =>
      createResolver(
        import(
          /* webpackChunkName: "my-care-book-appointment" */ '../my-care/container/book-appointment/book-appointment'
        ),
      ),
    restrictions: {
      user: [
        restrictions['care_plans'],
        restrictions['counsellor_access'],
        restrictions['counsellor_appointments'],
      ],
      operator: 'OR',
    },
    data: {
      title: 'appointments.your_counsellor.title',
      includeInMenu: false,
      hideFooter: true,
      authorizedRoles: [USER_ROLES.employee],
    },
  },
  tmhChat: {
    name: MyCareRoutes.CHAT,
    url: '/tmh/chat',
    resolver: () =>
      createResolver(
        import(
          /* webpackChunkName: "my-care-chat" */ '../chat/ccs/mobile/components/ccs-chat-mobile'
        ),
      ),
    restrictions: {
      user: [restrictions['care_coordination_specialists_messaging']],
    },
    data: {
      title: 'CCS Chat',
      includeInMenu: false,
      hideFooter: true,
      authorizedRoles: [USER_ROLES.employee],
    },
  },
  carePathHome: {
    name: MyCareRoutes.CARE_PATH_HOME,
    url: `/care-path-home`,
    resolver: () =>
      Promise.all([
        import(
          /* webpackChunkName: "my-care-care-path-home" */ './container/care-path-home/care-path-home'
        ),
        import(
          /* webpackChunkName: "thi-modal-reducer" */ 'react-components/thi-result/state/reducer'
        ),
      ]).then(([component, thiResult]) => {
        injectAsyncReducer('thiResult', thiResult.default);
        return component;
      }),
    restrictions: CarePathRestrictions,
    data: {
      title: 'care_path_home.title',
      checkIncludeInMenu: () => {
        return (
          !isFeatureEnabled(restrictions.care_plans) &&
          !isFeatureEnabled(restrictions.care_pathways)
        );
      },
      section: 'myCare',
      mainMenu: 'myCare',
      hideFooter: true,
      authorizedRoles: [USER_ROLES.employee],
    },
  },
};

export default myCareModule;
